import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBed,
  faChevronLeft,
  faChevronRight,
  faRulerCombined,
  faUser,
  faWifi,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";
import { useNavigate } from "react-router-dom";
import { calculateFinalPrice, formatPrice } from '../../utils/pricing';

const RoomCard = ({ room }) => {
  const navigate = useNavigate();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Early return if no room data
  if (!room) return null;

  const hasPhotos = room.photos && room.photos.length > 0;

  const nextPhoto = () => {
    if (!hasPhotos) return;
    setCurrentPhotoIndex((prev) =>
      prev === room.photos.length - 1 ? 0 : prev + 1
    );
  };

  const prevPhoto = () => {
    if (!hasPhotos) return;
    setCurrentPhotoIndex((prev) =>
      prev === 0 ? room.photos.length - 1 : prev - 1
    );
  };

  const handleReserve = async (rate) => {
    try {
      // Create quote for the selected rate
      const response = await fetch(`${config.backendServer}/api/stays/quotes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rate_id: rate.id,
        }),
      });

      const responseData = await response.json();

      // Log request and response
      console.log("📤 Quote Request:", {
        rate_id: rate.id,
      });
      console.log("📥 Quote Response:", responseData);

      // Check if the response contains an error
      if (!response.ok || responseData.error) {
        throw new Error(responseData.error || "Failed to create quote");
      }

      // Only navigate if we have valid quote data
      if (responseData.data) {
        navigate("/hotels/payment", {
          state: {
            quote: responseData.data,
            room: room,
            rate: rate, // Also pass the selected rate
          },
          replace: false,
        });
      } else {
        throw new Error("Invalid quote data received");
      }
    } catch (error) {
      console.error("Error creating quote:", error);
      // Add user-friendly error handling
      alert(`Failed to create quote: ${error.message}. Please try again.`);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden h-full">
      {/* Photo Gallery */}
      <div className="relative h-48">
        {hasPhotos ? (
          <img
            src={room.photos[currentPhotoIndex].url}
            alt={room.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-400">No image available</span>
          </div>
        )}

        {hasPhotos && room.photos.length > 1 && (
          <>
            <button
              onClick={prevPhoto}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              onClick={nextPhoto}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
              {currentPhotoIndex + 1} / {room.photos.length}
            </div>
          </>
        )}
      </div>

      {/* Room Details */}
      <div className="p-4">
        <div className="mb-3">
          <h3 className="text-lg font-semibold mb-2">
            {room.name || "Unnamed Room"}
          </h3>

          {/* Room Stats */}
          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faRulerCombined} />
              <span>258 sq ft</span>
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faUser} />
              <span>
                Sleeps{" "}
                {room.beds?.reduce((acc, bed) => acc + (bed.count || 0), 0) ||
                  "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faBed} />
              <span>
                {room.beds?.map((bed, index) => (
                  <span key={index}>
                    {bed.count || 0} {bed.type || "bed"}
                    {index < (room.beds?.length || 0) - 1 ? ", " : ""}
                  </span>
                )) || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faWifi} />
              <span>Free WiFi</span>
            </div>
          </div>
        </div>

        {/* Rates */}
        {room.rates?.map((rate, index) => (
          <div key={index} className="border-t pt-3 mt-3">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold mb-1 text-sm">
                  {(rate.board_type || "").replace(/_/g, " ")}
                </h4>
                {rate.cancellation_timeline?.length > 0 ? (
                  <p className="text-xs text-green-600">
                    Fully refundable before{" "}
                    {new Date(
                      rate.cancellation_timeline[0].before
                    ).toLocaleDateString()}
                  </p>
                ) : (
                  <p className="text-xs text-gray-600">Non-refundable</p>
                )}
              </div>

              <div className="text-right">
                <p className="text-xl font-bold">
                  {formatPrice(
                    calculateFinalPrice(rate.total_amount, true),
                    rate.currency || 'USD'
                  )}
                </p>
                <p className="text-xs text-gray-600">per stay</p>
                <button
                  onClick={() => handleReserve(rate)}
                  className={`${config.primaryColorBg} text-white px-4 py-1 rounded-full mt-2 text-sm hover:opacity-90 transition-opacity`}
                >
                  Reserve
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoomCard;
